import { Notifications, Notification } from 'common/types/notification.types'
import { useNotificationApi } from 'hooks/use-notification-api/useNotificationApi'
import { useEffect, useState } from 'react'
import { useLocalStorage } from 'hooks/use-local-storage/useLocalStorage'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
import { useHistory } from 'react-router-dom'
import { SocketEvent } from 'common/types'
import { useSocket } from 'hooks/use-socket/useSocket'

export const useNotifications = (userId: string) => {
  const history = useHistory()
  const { isDevice } = useScreenSizes()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [totalNotifications, setTotalNotifications] = useState<number>()
  const [drawer, setDrawer] = useState(false)
  const [page, setPage] = useState(1)
  const [showNotificationAlert, setShowNotificationAlert] = useState<boolean>(false)
  const { getItem, setItem } = useLocalStorage()
  const { fetchAllNotifications, updateNotification, clearNotifications } = useNotificationApi()
  const { get, socket } = useSocket()

  const getNotifications = async () => {
    const fetchedNotifications: Notifications = await fetchAllNotifications(userId, page, false, 10)

    if (page > 1) {
      setTotalNotifications(Number(fetchedNotifications?.totalCount))
      return
    }

    setTotalNotifications(Number(fetchedNotifications?.totalCount))
    setNotifications(fetchedNotifications?.data)
  }

  const getMoreNotifications = async () => {
    const notificationsList: Notifications = await fetchAllNotifications(userId, page, false, 10)
    const uniqueNotification = notificationsList?.data?.filter(x => {
      const existingNotification = notifications?.find(y => y?.id === x?.id)
      if (!existingNotification) return x
    })
    setTotalNotifications(Number(notificationsList?.totalCount))
    setNotifications(notifications?.concat(uniqueNotification))
  }

  const handleScrollPings = async e => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight
    if (bottom) {
      const fetchedNotifications: Notifications = await fetchAllNotifications(
        userId,
        bottom ? page + 1 : page,
        false,
        10
      )
      if (Number(fetchedNotifications?.totalCount) > notifications.length) {
        setPage(page + 1)
        setNotifications([...notifications, ...fetchedNotifications?.data])
      }
    }
  }

  useEffect(() => {
    if (userId) {
      getNotifications()
    }
  }, [drawer])

  useEffect(() => {
    if (userId) {
      getMoreNotifications()
    }
  }, [page])

  useEffect(() => {
    const notificationInStorage = getItem('notifications')
    let notificationReceived
    let lastCreatedAt = ''
    let sentBy = ''
    if (notificationInStorage) {
      notificationReceived = JSON.parse(notificationInStorage)
      lastCreatedAt = notificationReceived.createdAt
      sentBy = notificationReceived.sender
    }

    if (notifications?.length > 0) {
      if (notifications[0].createdAt <= lastCreatedAt) {
        setShowNotificationAlert(false)
      } else {
        setShowNotificationAlert(true)
      }
    } else if (!notificationInStorage && notifications?.some(alert => alert.viewed !== true)) {
      setShowNotificationAlert(true)
    }
  }, [notifications, drawer])

  useEffect(() => {
    get(SocketEvent.NotificationReceived, (notification: Notification) => {
      if(notifications) {
        setNotifications(prev => [notification, ...prev])
      }
    })
    return () => {
      socket.off(SocketEvent.NotificationReceived)
    }
  }, [])

  const handleStoringLatestNotification = () => {
    setShowNotificationAlert(false)
    if (notifications.length > 0) {
      const store = {
        createdAt: notifications[0]?.createdAt,
        sender: notifications[0]?.sender
      }
      setItem('notifications', JSON.stringify(store))
    }
  }

  const handleClick = () => {
    if (isDevice) {
      history.push('/pings')
    } else {
      setDrawer(true)
    }
    handleStoringLatestNotification()
  }

  return {
    unreadNotifications: notifications,
    drawer,
    totalNotifications,
    setDrawer,
    handleScrollPings,
    setPage,
    page,
    notifications,
    updateNotification,
    setNotifications,
    setTotalNotifications,
    clearNotifications,
    showNotificationAlert,
    setShowNotificationAlert,
    handleAlertClick: handleClick,
    handleStoringLatestNotification
  } as const
}
